import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import dayjs, * as day from 'dayjs';
import { FlexTimePeriod, FlexTimePeriodPayload } from 'libs/domain';
import { BehaviorSubject, ReplaySubject, of } from 'rxjs';
import { share, takeUntil } from 'rxjs/operators';

import { MgValidators } from '@app/src/app/input/validators';

import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayPrimaryHeaderBackground,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { FlexTimePeriodService, flexCloneData } from '@shared/services/flex-time';

import { setDefaultRelativeTimeControlValue } from '../../../../../../shared/components/form-relative-time-input/form-relative-time-input.constants';
import {
  RelativeTimeForm,
  RelativeTimeValues,
} from '../../../../../../shared/components/form-relative-time-input/form-relative-time-input.types';
import { FtmPeriodsEditFormFields, FtmPeriodsMessage } from '../../constants';
import { FtmPeriodsService } from '../../services';
import { FtmPeriodsEditForm, FtmPeriodsEditModalData, FtmPeriodsEditModalResponse } from '../../types';

@Component({
  selector: 'mg-ftm-periods-edit',
  templateUrl: './ftm-periods-edit.component.html',
  styleUrls: ['./ftm-periods-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmPeriodsEditComponent implements OnInit, OnDestroy {
  /** Constants */
  public readonly MESSAGES = FtmPeriodsMessage;
  public readonly MODAL_CONFIG = {
    headerBg: ModalOverlayPrimaryHeaderBackground.ALT_TEAL,
  };
  public readonly FORM_FIELD = FtmPeriodsEditFormFields;

  /** General Observables */
  private _destroyed = new ReplaySubject<void>(1);

  /** Loading */
  private _isLoading = new BehaviorSubject<boolean>(false);
  public readonly isLoading$ = this._isLoading.asObservable();
  private _isSubmitting = new BehaviorSubject<boolean>(false);
  public readonly isSubmitting$ = this._isSubmitting.asObservable();

  /** Modal */
  public readonly modalTitle$ = of(this.dialogData.id && !this.dialogData.isClone ? 'Edit Period' : 'New Period');

  /** Is New Period */
  private _isNew = this.dialogData.id >= 0 && !this.dialogData.isClone ? false : true;
  public isNew$ = of(this._isNew);
  public currentDate = day();

  // we define this at the component level so the start/end times are evaluated on component mount instead on bundle load (in constant file)
  public FORM_GROUP = {
    [FtmPeriodsEditFormFields.TITLE]: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
    [FtmPeriodsEditFormFields.DATE]: [null, [Validators.required, MgValidators.DateNotInPastValidator]],
    [FtmPeriodsEditFormFields.START_TIME]: [day().add(1, 'h').format('HH:mm'), [Validators.required]],
    [FtmPeriodsEditFormFields.END_TIME]: [day().add(2, 'h').format('HH:00'), [Validators.required]],
    [FtmPeriodsEditFormFields.ACTIVITY_MANAGE]: [true],
    [FtmPeriodsEditFormFields.STUDENT_MANAGE]: [true],
    [FtmPeriodsEditFormFields.STUDENT_SELF_MANAGE]: [false],
    [FtmPeriodsEditFormFields.CLONE_REGISTERED]: [true],
    [FtmPeriodsEditFormFields.CLONE_ASSIGNED]: [true],
    [FtmPeriodsEditFormFields.ENABLE_TEACHER_DEADLINE]: [false],
    [FtmPeriodsEditFormFields.ENABLE_STUDENT_DEADLINE]: [false],
    [FtmPeriodsEditFormFields.TEACHER_DEADLINE]: setDefaultRelativeTimeControlValue(),
    [FtmPeriodsEditFormFields.STUDENT_DEADLINE]: setDefaultRelativeTimeControlValue(),
  };

  /** Form Controls */
  public startTimeExternalError = false;
  public endTimeExternalError = false;

  public readonly form: FormGroup<FtmPeriodsEditForm> = this._fb.group(this.FORM_GROUP, {
    validators: this._isNew
      ? [
          MgValidators.TimeComparisonValidator('startTime', 'endTime'),
          // lets only validate date/time not in past on new periods
          MgValidators.DateTimeNotInPastValidator(this.FORM_FIELD.DATE, this.FORM_FIELD.START_TIME),
        ]
      : [MgValidators.TimeComparisonValidator('startTime', 'endTime')],
  });

  private updateStartTimeAndEndTimeExternalErrors(): void {
    const hasTimeComparisonError = this.form.hasError('invalidTimeComparison');
    const hasDateTimeInPastError = this.form.hasError('invalidDateTimeInPast');
    this.startTimeExternalError = hasTimeComparisonError || hasDateTimeInPastError;
    this.endTimeExternalError = hasTimeComparisonError || hasDateTimeInPastError;
  }

  /** Period */
  private readonly _period = new BehaviorSubject<FlexTimePeriod | undefined>(undefined);
  public readonly period$ = this._period.asObservable().pipe(share());

  /** Computed Getters */
  get canSubmit(): boolean {
    return this.form.valid && (this.form.dirty || this.dialogData.isClone);
  }

  get minTime(): string {
    return this.form.get(this.FORM_FIELD.DATE) &&
      this.form.get(this.FORM_FIELD.DATE).value?.isSameOrBefore(this.currentDate, 'day')
      ? this.currentDate.format('HH:mm')
      : null;
  }

  /** Component Constructor */
  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    public dialogData: FtmPeriodsEditModalData,
    private _modalRef: ModalOverlayRef<FtmPeriodsEditModalData, FtmPeriodsEditModalResponse>,
    private _dialog: MatDialog,
    private _fb: UntypedFormBuilder,
    private _flexPeriods: FtmPeriodsService,
    private _ftPeriods: FlexTimePeriodService,
  ) {
    if (!this.dialogData.isClone && !this.dialogData.id) {
      this.form.controls[this.FORM_FIELD.DATE].setValue(day());
    }
  }

  ngOnInit(): void {
    this._initPeriodData(this.dialogData?.id);
    this.updateStartTimeAndEndTimeExternalErrors();
    this.form.statusChanges.pipe(
      takeUntil(this._destroyed)
    ).subscribe(() => {
      this.updateStartTimeAndEndTimeExternalErrors();
    });
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this._isLoading.complete();
    this._isSubmitting.complete();
  }

  public async submit(): Promise<void> {
    if (!this.canSubmit) return;
    this._isSubmitting.next(true);
    const payload = this._getPayload();
    if (this._isNew) {
      let cloneData: flexCloneData;
      if (this.dialogData.isClone) {
        cloneData = {
          cloneRegistered: this.form.controls.cloneRegistered.value,
          cloneAssigned: this.form.controls.cloneAssigned.value,
        };
      }
      await this._flexPeriods.create(payload, cloneData);

      this._modalRef.close(ModalOverlayServiceCloseEventType.CREATE);
    } else {
      await this._flexPeriods.update(payload);
      this._modalRef.close(ModalOverlayServiceCloseEventType.SUBMIT);
    }
    this._isSubmitting.next(false);
  }

  public async delete(): Promise<void> {
    // omit confirmation modal on create
    if (this._isNew) {
      this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
      return;
    }

    const confirmationDialog = this._dialog.open(ConfirmationDialogComponent, {
      data: {
        text: {
          description: `Are you sure you want to delete this flex time period?`,
          deleteBtn: 'Delete',
        },
      },
    });
    confirmationDialog.afterClosed().subscribe(async response => {
      if (!response || !response.confirmed || response.cancelled) return;

      const period = this._period.getValue();
      this._flexPeriods.delete(period.id);
      this._modalRef.close(ModalOverlayServiceCloseEventType.DELETE);
    });
  }

  private async _initPeriodData(id?: number): Promise<void> {
    if (!id) return;
    try {
      this._isLoading.next(true);
      const result = await this._ftPeriods.fetch(id);
      if (this.dialogData.isClone) {
        result.title += ' Clone';
      }
      this._period.next({ ...result });
      this._setFormValues(result);
    } catch (error) {
      this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
    } finally {
      if (this.dialogData.isClone) this.form.markAsDirty();
      this._isLoading.next(false);
    }
  }

  private _setFormValues(period: FlexTimePeriod) {
    for (const key in period) {
      if (period.hasOwnProperty(key) && this.form.controls[key]) {
        if (key === FtmPeriodsEditFormFields.DATE) {
          period[key] = day(period[key]) as any;
        }

        if (key === FtmPeriodsEditFormFields.TEACHER_DEADLINE || key === FtmPeriodsEditFormFields.STUDENT_DEADLINE) {
          const deadline = period[key];
          const startTime = period.startTime;

          if (deadline && startTime) {
            const deadlineFormGroup = this.form.get(key) as FormGroup<RelativeTimeForm>;
            this._deadlineToRelativeTimeInput(deadline, startTime, deadlineFormGroup);
          }

          if (key === FtmPeriodsEditFormFields.TEACHER_DEADLINE) {
            this.form.controls[FtmPeriodsEditFormFields.ENABLE_TEACHER_DEADLINE].setValue(!!deadline);
          } else if (key === FtmPeriodsEditFormFields.STUDENT_DEADLINE) {
            this.form.controls[FtmPeriodsEditFormFields.ENABLE_STUDENT_DEADLINE].setValue(!!deadline);
          }
        } else {
          this.form.get(key).setValue(period[key]);
        }
      }
    }
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private _getPayload(): FlexTimePeriodPayload {
    const title = this.form.controls[FtmPeriodsEditFormFields.TITLE].value;
    const date = this.form.controls[FtmPeriodsEditFormFields.DATE].value;
    const startTime = this.form.controls[FtmPeriodsEditFormFields.START_TIME].value;
    const endTime = this.form.controls[FtmPeriodsEditFormFields.END_TIME].value;

    const enableActivityManagement = this.form.controls[FtmPeriodsEditFormFields.ACTIVITY_MANAGE].value;

    const enableStudentManagement = this.form.controls[FtmPeriodsEditFormFields.STUDENT_MANAGE].value;
    const teacherRegistrationDeadline = this.form.controls[FtmPeriodsEditFormFields.TEACHER_DEADLINE].value;
    const enableTeacherRegistrationDeadline =
      this.form.controls[FtmPeriodsEditFormFields.ENABLE_TEACHER_DEADLINE].value;

    const enableStudentSelfManagement = this.form.controls[FtmPeriodsEditFormFields.STUDENT_SELF_MANAGE].value;
    const studentRegistrationDeadline = this.form.controls[FtmPeriodsEditFormFields.STUDENT_DEADLINE].value;
    const enableStudentRegistrationDeadline =
      this.form.controls[FtmPeriodsEditFormFields.ENABLE_STUDENT_DEADLINE].value;

    const payload: FlexTimePeriodPayload = {
      ...this._period.getValue(),
      title,
      date: date ? dayjs(date).startOf('day').toDate() : null,
      startTime,
      endTime,
      enableActivityManagement,
      enableStudentManagement,
      enableStudentSelfManagement,
      teacherRegistrationDeadline:
        enableTeacherRegistrationDeadline && enableStudentManagement
          ? this._createDeadline(date.toDate(), startTime, teacherRegistrationDeadline as RelativeTimeValues)
          : null,
      studentRegistrationDeadline:
        enableStudentRegistrationDeadline && enableStudentSelfManagement
          ? this._createDeadline(date.toDate(), startTime, studentRegistrationDeadline as RelativeTimeValues)
          : null,
    };

    return payload;
  }

  /**
   * Use the date/time fields and the controls in the relative time input
   * to create the deadline as a dayjs object to send to the backend
   */
  private _createDeadline(date: Date, startTime: string, deadlineValues: RelativeTimeValues): day.Dayjs {
    // turn date and startTime fields into proper dayjs object
    const start = dayjs(date)
      .set('hour', +startTime.split(':')[0])
      .set('minute', +startTime.split(':')[1])
      .startOf('minute');

    if (deadlineValues.timeDirection === 'before') {
      return start.subtract(deadlineValues.timeValue, deadlineValues.timeUnit as day.ManipulateType);
    } else {
      return start.add(deadlineValues.timeValue, deadlineValues.timeUnit as day.ManipulateType);
    }
  }

  /**
   * Turn the date/time data and deadline dayjs object from the backend
   * into the readable values for the relative time input
   */
  private _deadlineToRelativeTimeInput(deadline: day.Dayjs, startTime: string, deadlineFormGroup: FormGroup): void {
    if (!deadline) return;

    // turn date and startTime fields into proper dayjs object
    const periodDate = this.form.get(FtmPeriodsEditFormFields.DATE).value;
    const start = dayjs(periodDate)
      .set('hour', +startTime.split(':')[0])
      .set('minute', +startTime.split(':')[1])
      .startOf('minute');

    const diff = deadline.diff(start, 'minute');
    const isBefore = diff < 0;

    const absoluteDiff = Math.abs(diff);
    const deadlineNumber = absoluteDiff >= 60 ? Math.floor(absoluteDiff / 60) : absoluteDiff;
    const deadlineSelectTime = absoluteDiff >= 60 ? 'hours' : 'minutes';
    const deadlineSelectWhen = isBefore ? 'before' : 'after';

    deadlineFormGroup.get('timeValue').setValue(deadlineNumber);
    deadlineFormGroup.get('timeUnit').setValue(deadlineSelectTime);
    deadlineFormGroup.get('timeDirection').setValue(deadlineSelectWhen);
  }
}
